import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import ServiceTypeToggle from '../components/services/ServiceTypeToggle';
import PricePreview from '../components/services/PricePreview';
import { arabicErrorFor } from '../utils/serviceErrorMessages';
import '../styles/CreateServiceClient.css'; // Reuse wizard styles

// Step 6.8 — adds dual-billing mode (subscription / per_visit) per decision #48.
// Auto-detects: location with active subscription → defaults to subscription
// (toggle exposed); location without subscription → forces per_visit silently.
// Per-visit path uses catalog perVisitBasic / perVisitDeep tables, atomically
// creates ServiceRequest + Invoice on the backend (Stage 3 step 9).

const CreateServiceRequest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeNavItem, setActiveNavItem] = useState('services');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  // Form
  const [form, setForm] = useState({
    clientId: searchParams.get('clientId') || '',
    locationId: '',
    requestedDate: '',
    requestedTimeSlot: '',
    notes: '',
    billingMode: 'subscription', // auto-corrected by effect when location changes
    serviceType: 'basic',
    discountAmount: ''
  });

  // Per-visit live quote state (display-only; server recomputes on submit)
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await serviceApi.getClients();
        const clientList = (res.data?.clients || []).filter(c => c.status === 'active');
        setClients(clientList);
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Fetch locations + subscriptions when client changes
  useEffect(() => {
    if (!form.clientId) {
      setLocations([]);
      setSubscriptions([]);
      return;
    }

    const fetchClientData = async () => {
      try {
        const [locsRes, subsRes] = await Promise.all([
          serviceApi.getClientLocations(form.clientId),
          serviceApi.getClientSubscriptions(form.clientId),
        ]);
        setLocations((locsRes.data?.locations || []).filter(l => l.status === 'active'));
        setSubscriptions((subsRes.data?.subscriptions || []).filter(s => s.status === 'active'));
      } catch (err) {
        console.error('Error fetching client data:', err);
      }
    };
    fetchClientData();
  }, [form.clientId]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Selected location + its active subscription (if any)
  const selectedLocation = useMemo(
    () => locations.find(l => l._id === form.locationId) || null,
    [locations, form.locationId]
  );
  const subForLocation = useMemo(
    () => subscriptions.find(s => (s.locationId?._id || s.locationId) === form.locationId) || null,
    [subscriptions, form.locationId]
  );
  const hasActiveSub = !!subForLocation;

  // Auto-correct billingMode when location changes — force per_visit if no sub.
  useEffect(() => {
    if (!form.locationId) return;
    if (hasActiveSub && form.billingMode === 'subscription') return; // valid combo
    if (hasActiveSub) {
      handleChange('billingMode', 'subscription');
    } else {
      handleChange('billingMode', 'per_visit');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.locationId, hasActiveSub]);

  // Debounced quote fetch when in per_visit mode
  useEffect(() => {
    if (form.billingMode !== 'per_visit' || !selectedLocation?.pricingTier) {
      setQuote(null);
      return undefined;
    }
    const handler = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const res = await serviceApi.getCatalogQuote({
          pricingTier: selectedLocation.pricingTier,
          serviceType: form.serviceType,
          discountAmount: form.discountAmount || 0
        });
        setQuote(res.data);
      } catch (_err) {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [form.billingMode, form.serviceType, form.discountAmount, selectedLocation?.pricingTier]);

  // Subscription balance (only meaningful in subscription mode)
  const balance = hasActiveSub
    ? (subForLocation.effectiveBalance ?? (subForLocation.visitsIncluded - subForLocation.visitsUsed))
    : null;
  const paymentStatus = subForLocation?.paymentStatus || null;

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!form.clientId || !form.locationId || !form.requestedDate) {
      setError('العميل والموقع والتاريخ مطلوبة');
      return;
    }

    if (form.billingMode === 'subscription' && balance !== null && balance <= 0) {
      setError('لا يوجد رصيد كافٍ — يرجى تجديد الاشتراك أولاً');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clientId: form.clientId,
        locationId: form.locationId,
        requestedDate: form.requestedDate,
        requestedTimeSlot: (form.requestedTimeSlot && form.requestedTimeSlot !== 'custom') ? form.requestedTimeSlot : undefined,
        notes: form.notes || undefined
      };
      if (form.billingMode === 'per_visit') {
        payload.billingMode = 'per_visit';
        payload.serviceType = form.serviceType;
        if (form.discountAmount) payload.discountAmount = parseFloat(form.discountAmount);
      }

      const res = await serviceApi.createRequest(payload);
      const ticketId = res.data?.task?.ticketId || '';
      const invoiceNumber = res.data?.invoice?.invoiceNumber || '';
      const parts = ['تم إنشاء الطلب بنجاح'];
      if (ticketId) parts.push(`المهمة: ${ticketId}`);
      if (invoiceNumber) parts.push(`الفاتورة: ${invoiceNumber}`);
      setSuccess(parts.join(' — '));

      // Refetch subscriptions after subscription-mode submit so the balance
      // display reflects the just-decremented value (per_visit submits don't
      // touch balance — skip the refetch).
      if (form.billingMode === 'subscription' && form.clientId) {
        try {
          const subsRes = await serviceApi.getClientSubscriptions(form.clientId);
          setSubscriptions((subsRes.data?.subscriptions || []).filter(s => s.status === 'active'));
        } catch (_e) { /* non-fatal — stale balance won't block the user */ }
      }
    } catch (err) {
      setError(arabicErrorFor(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="طلب نظافة جديد"
    >
      <div className="svc-wizard">
        <button className="svc-wizard__back" onClick={() => navigate('/services')}>
          <i className="bi bi-arrow-right" />
          العودة
        </button>

        <div className="svc-wizard__step-title">طلب نظافة جديد</div>
        <div className="svc-wizard__step-sub">اختر العميل والموقع والتاريخ</div>

        {success && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 500, fontSize: '0.9rem' }}>
            <i className="bi bi-check-circle" style={{ marginInlineStart: 6 }} />
            {success}
          </div>
        )}

        {error && (
          <div className="svc-wizard__error" style={{ marginBottom: 16 }}>
            <i className="bi bi-exclamation-circle" />
            {error}
          </div>
        )}

        {paymentStatus === 'overdue' && form.billingMode === 'subscription' && (
          <div style={{ background: '#fff3cd', color: '#856404', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem', fontWeight: 500 }}>
            <i className="bi bi-exclamation-triangle" style={{ marginInlineStart: 6 }} />
            تنبيه: الدفعة متأخرة لهذا الاشتراك
          </div>
        )}

        <div className="svc-wizard__form">
          {/* Client picker */}
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">العميل *</label>
            <select
              className="svc-wizard__input svc-wizard__input--select touch-input"
              value={form.clientId}
              onChange={(e) => {
                handleChange('clientId', e.target.value);
                handleChange('locationId', '');
              }}
            >
              <option value="">اختر العميل...</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>
                  {c.userId?.name || '—'} {c.userId?.clientCode ? `(${c.userId.clientCode})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Location picker */}
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">الموقع *</label>
            <select
              className="svc-wizard__input svc-wizard__input--select touch-input"
              value={form.locationId}
              onChange={(e) => handleChange('locationId', e.target.value)}
              disabled={!form.clientId}
            >
              <option value="">اختر الموقع...</option>
              {locations.map(loc => (
                <option key={loc._id} value={loc._id}>
                  {loc.name} ({loc.locationCode})
                  {loc.pricingTier ? ` — ${loc.pricingTier}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Billing mode — only shown when both modes are valid (i.e., sub exists).
              When no sub: per_visit is forced silently with an info note. */}
          {form.locationId && hasActiveSub && (
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">طريقة الفوترة</label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="svc-action"
                  style={{
                    flex: 1,
                    background: form.billingMode === 'subscription' ? '#471396' : '#fff',
                    color: form.billingMode === 'subscription' ? '#fff' : '#471396',
                    borderColor: '#471396'
                  }}
                  onClick={() => handleChange('billingMode', 'subscription')}
                >
                  <i className="bi bi-bookmark-check" />
                  من الاشتراك
                </button>
                <button
                  type="button"
                  className="svc-action"
                  style={{
                    flex: 1,
                    background: form.billingMode === 'per_visit' ? '#471396' : '#fff',
                    color: form.billingMode === 'per_visit' ? '#fff' : '#471396',
                    borderColor: '#471396'
                  }}
                  onClick={() => handleChange('billingMode', 'per_visit')}
                >
                  <i className="bi bi-receipt" />
                  زيارة فردية (فاتورة منفصلة)
                </button>
              </div>
            </div>
          )}

          {/* No-subscription notice — informs user per_visit is the only option */}
          {form.locationId && !hasActiveSub && (
            <div style={{
              fontSize: '0.85rem', padding: '10px 14px', borderRadius: 8,
              fontWeight: 500, background: '#e3f2fd', color: '#1565c0'
            }}>
              <i className="bi bi-info-circle" style={{ marginInlineStart: 6 }} />
              لا يوجد اشتراك نشط لهذا الموقع — سيتم إصدار فاتورة فردية لهذه الزيارة
            </div>
          )}

          {/* SUBSCRIPTION MODE — show balance */}
          {form.billingMode === 'subscription' && hasActiveSub && (
            <div style={{
              fontSize: '0.88rem', padding: '10px 14px', borderRadius: 8,
              fontWeight: 600,
              background: balance <= 0 ? '#ffebee' : balance <= 2 ? '#fff3e0' : '#e8f5e9',
              color: balance <= 0 ? '#c62828' : balance <= 2 ? '#e65100' : '#2e7d32',
            }}>
              <i className="bi bi-wallet2" style={{ marginInlineStart: 6 }} />
              الرصيد المتاح: {balance} زيارة
            </div>
          )}

          {/* PER-VISIT MODE — service type + discount + live price preview */}
          {form.billingMode === 'per_visit' && form.locationId && (
            <>
              <ServiceTypeToggle
                value={form.serviceType}
                onChange={(v) => handleChange('serviceType', v)}
              />
              <div className="svc-wizard__field">
                <label className="svc-wizard__label">الخصم (ر.س)</label>
                <input
                  className="svc-wizard__input touch-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discountAmount}
                  onChange={(e) => handleChange('discountAmount', e.target.value)}
                  placeholder="0"
                  dir="ltr"
                  style={{ maxWidth: 200 }}
                />
              </div>
              {(quote || quoteLoading) && (
                <PricePreview commercials={quote?.commercials} loading={quoteLoading} />
              )}
            </>
          )}

          {/* Date + time */}
          <div className="svc-wizard__row">
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">تاريخ النظافة *</label>
              <input
                className="svc-wizard__input touch-input"
                type="date"
                value={form.requestedDate}
                onChange={(e) => handleChange('requestedDate', e.target.value)}
                dir="ltr"
              />
            </div>
            <div className="svc-wizard__field">
              <label className="svc-wizard__label">الوقت المفضل</label>
              <select
                className="svc-wizard__input svc-wizard__input--select touch-input"
                value={['', '08:00', '10:00', '13:00', '16:00'].includes(form.requestedTimeSlot) ? form.requestedTimeSlot : 'custom'}
                onChange={(e) => handleChange('requestedTimeSlot', e.target.value)}
              >
                <option value="">غير محدد</option>
                <option value="08:00">صباحاً (8:00)</option>
                <option value="10:00">ضحى (10:00)</option>
                <option value="13:00">ظهراً (1:00)</option>
                <option value="16:00">عصراً (4:00)</option>
                <option value="custom">أخرى</option>
              </select>
              {form.requestedTimeSlot && !['', '08:00', '10:00', '13:00', '16:00'].includes(form.requestedTimeSlot) && (
                <input
                  type="time"
                  className="svc-wizard__input touch-input"
                  style={{ marginTop: 8, maxWidth: 160 }}
                  value={form.requestedTimeSlot === 'custom' ? '' : form.requestedTimeSlot}
                  onChange={(e) => handleChange('requestedTimeSlot', e.target.value || 'custom')}
                  dir="ltr"
                />
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="svc-wizard__field">
            <label className="svc-wizard__label">ملاحظات</label>
            <textarea
              className="svc-wizard__input touch-input"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              placeholder="تعليمات خاصة..."
            />
          </div>
        </div>

        <div className="svc-wizard__actions" style={{ marginTop: 24 }}>
          <button
            className="svc-action svc-action--primary"
            onClick={handleSubmit}
            disabled={
              saving ||
              (form.billingMode === 'subscription' && balance !== null && balance <= 0)
            }
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-check-lg" />
            )}
            إنشاء الطلب
          </button>
          {success && (
            <button
              className="svc-action svc-action--outline"
              onClick={() => {
                setForm({
                  clientId: form.clientId, locationId: '', requestedDate: '',
                  requestedTimeSlot: '', notes: '',
                  billingMode: 'subscription', serviceType: 'basic', discountAmount: ''
                });
                setQuote(null);
                setSuccess(null);
              }}
            >
              <i className="bi bi-plus" />
              طلب آخر
            </button>
          )}
        </div>
      </div>
    </DesktopLayout>
  );
};

export default CreateServiceRequest;
